/**
 * yyyyMMddHHmmss → "YYYY년 M월 D일 H시" 형태로 변환
 *
 * @param {string} rawDate - 예: "20251202154926"
 * @returns {string} 변환된 날짜 문자열
 */
function formatDate(rawDate) {
  if (!rawDate || rawDate.length !== 14) {
    return '잘못된 날짜 형식';
  }

  const year = rawDate.slice(0, 4);
  const month = parseInt(rawDate.slice(4, 6), 10);
  const day = parseInt(rawDate.slice(6, 8), 10);
  const hour = parseInt(rawDate.slice(8, 10), 10);
  // 분, 초는 slice(10,12), slice(12,14)인데 여기서는 사용하지 않음

  return `${year}년 ${month}월 ${day}일`;
}

/**
 * 최근 N개월 범위의 기본 조회 기간을 반환하는 유틸 함수
 * - yyyy-mm-dd 형식으로 날짜를 반환
 */
function getDefaultDateRange(months = 3) {
  const end = new Date();
  const start = new Date();
  start.setMonth(end.getMonth() - months);
  const toStr = (d) => d.toLocaleDateString('sv-SE');
  return { start: toStr(start), end: toStr(end) };
}

/**
 * YYYY.MM.DD → 다양한 날짜 포맷으로 변환
 *
 * @param {string} dateString - "2025.12.08" 형태
 * @param {object} options
 *   - separator: 구분자("/", "-", ".", "_")
 *   - style: "separator" | "korean"
 * @returns {string}
 */
function formatSimpleDate(dateString, options = { style: 'separator', separator: '/' }) {
  if (!dateString || dateString.length !== 10) {
    return '잘못된 날짜 형식';
  }

  const year = dateString.substring(0, 4);
  const month = dateString.substring(5, 7);
  const day = dateString.substring(8, 10);

  if (options.style === 'korean') {
    return `${year}년 ${month}월 ${day}일`;
  }

  const sep = options.separator ?? '/';
  return `${year}${sep}${month}${sep}${day}`;
}

/**
 * Date 객체를 문자열로 변환 (YYYY-MM-DD)
 *
 * @param date Date 객체
 * @param sep - 구분자 (기본값: '-')
 * @returns {string}
 */
function formatDateToYMD(date, sep = '-') {
  if (!date) return '';

  // Date 객체인지 확인하고 유효한 날짜인지 체크
  if (!(date instanceof Date) || isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}${sep}${month}${sep}${day}`;
}

/**
 * 날짜 문자열을 YYYY-MM-DD 형식으로 변환
 * - YYYYMMDDHHMMSS 형식 또는 YYYY-MM-DD 형식을 지원
 *
 * @param {string} dateString - 날짜 문자열
 * @returns {string} - YYYY-MM-DD 형식의 날짜 문자열
 */
function formatDateToYYYYMMDD(dateString) {
  if (!dateString) {
    return '-';
  }

  if (typeof dateString === 'string') {
    // 이미 YYYY-MM-DD 형식인 경우
    if (dateString.includes('-') && dateString.length >= 10) {
      return dateString.substring(0, 10);
    }

    // YYYYMMDDHHMMSS 형식인 경우 (14자리 또는 8자리 이상)
    if (dateString.length >= 8 && !dateString.includes('-')) {
      const year = dateString.substring(0, 4);
      const month = dateString.substring(4, 6);
      const day = dateString.substring(6, 8);
      return `${year}-${month}-${day}`;
    }
  }

  return '-';
}

/**
 * 날짜 문자열을 YYYY-MM-DD HH:mm:ss 형식으로 변환
 * - YYYYMMDDHHmmss 형식 또는 YYYY-MM-DD HH:mm:ss 형식을 지원
 *
 * @param {string} dateString
 * @returns {string}
 */
function formatDateToYYYYMMDDHHmmss(dateString) {
  if (!dateString || typeof dateString !== 'string') {
    return '-';
  }

  // 이미 YYYY-MM-DD HH:mm:ss 형식인 경우
  if (dateString.includes('-') && dateString.includes(':') && dateString.length >= 19) {
    return dateString.substring(0, 19);
  }

  // YYYYMMDDHHmmss 형식 (14자리 이상)
  if (!dateString.includes('-') && dateString.length >= 14) {
    const year = dateString.substring(0, 4);
    const month = dateString.substring(4, 6);
    const day = dateString.substring(6, 8);
    const hour = dateString.substring(8, 10);
    const minute = dateString.substring(10, 12);
    const second = dateString.substring(12, 14);

    return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
  }

  // YYYYMMDD 형식만 들어온 경우
  if (!dateString.includes('-') && dateString.length === 8) {
    const year = dateString.substring(0, 4);
    const month = dateString.substring(4, 6);
    const day = dateString.substring(6, 8);

    return `${year}-${month}-${day} 00:00:00`;
  }

  return '-';
}

export {
  formatDate,
  getDefaultDateRange,
  formatSimpleDate,
  formatDateToYMD,
  formatDateToYYYYMMDD,
  formatDateToYYYYMMDDHHmmss,
};
